import React, { useEffect, useState } from 'react';
import cx from 'classnames';
import { useSelector } from 'react-redux';
import { MultiSelect } from 'app/shared/select';
import { Button } from 'app/shared/button';
import { DetailItem } from 'app/shared/info-section/item';
import { GridColumn, GridRow } from 'app/layouts/grid';
import { Spacer } from 'app/layouts/generic';
import { toReadableNumber } from 'app/shared/utils/number';
import { useModuleService } from 'hooks/sales/module';
import { useCurrencyService } from 'hooks/sales/currency';
import { usePaymentService } from 'hooks/sales/payment';
import { useTaxService } from 'hooks/sales/tax';
import { useAgencySalesService } from 'hooks/sales/agency';
import { InfoBanner } from 'app/shared/info-banner';
import { prepareCheckoutConfig } from 'app/member/modules/wallet/helper';
import styles from './subscription.module.css';

const DEFAULT_CURRENCY = 'NGN';

const RENEWAL_PERIODS = [
  { label: 'Monthly', value: 'month' },
  { label: 'Annually', value: 'year' }
];

// Agency discount as a fraction of the plan price, by tier + renewal period.
const agencyDiscountRate = (tier, renewal) => {
  if (tier === 'professional') return renewal === 'year' ? 0.1 : 0.05;
  if (tier === 'enterprise') return renewal === 'year' ? 0.15 : 0.05;
  return 0; // explorer (and any other tier) — no discount
};

const bracketsFromPlan = (plan) =>
  (plan?.pricing || [])
    .map((pricing) => ({
      min: pricing.contact_bracket.min,
      max: pricing.contact_bracket.max,
      value: `${pricing.contact_bracket.min}:${pricing.contact_bracket.max}`,
      label: `${toReadableNumber(pricing.contact_bracket.min)} - ${toReadableNumber(
        pricing.contact_bracket.max
      )}`
    }))
    .sort((a, b) => Number(a.min) - Number(b.min));

export const BuySubscriptionModal = ({ organization = {}, closeModal = () => {} }) => {
  const agency = useSelector((state) => state.metadata.agency);
  const { fetchModules } = useModuleService();
  const { fetchCurrencies, getCurrencySymbol } = useCurrencyService();
  const { createPayment } = usePaymentService();
  const { fetchTaxes } = useTaxService();
  const { fetchActiveSubscriptions } = useAgencySalesService();

  const [checking_subscription, setCheckingSubscription] = useState(false);
  const [has_active_subscription, setHasActiveSubscription] = useState(false);
  const [tax_rates, setTaxRates] = useState({});
  const [modules, setModules] = useState([]);
  const [selected_plan, setSelectedPlan] = useState(null);
  const [currencies, setCurrencies] = useState([]);
  const [selected_currency, setSelectedCurrency] = useState(null);
  const [contact_brackets, setContactBrackets] = useState([]);
  const [selected_bracket, setSelectedBracket] = useState(null);
  const [selected_renewal, setSelectedRenewal] = useState(RENEWAL_PERIODS[0]);
  const [showing_summary, setShowingSummary] = useState(false);
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    fetchModules().then(({ modules: fetched }) => {
      const sorted = (fetched || []).sort((a, b) => a.tier_index - b.tier_index);
      const options = sorted.map((module) => ({
        ...module,
        label: module.title,
        value: module.tier
      }));
      setModules(options);
      if (options.length) setSelectedPlan(options[0]);
    });

    fetchCurrencies().then(({ currencies: fetched }) => {
      const options = (fetched || []).map((currency) => ({
        ...currency,
        value: currency.code,
        label: currency.code
      }));
      setCurrencies(options);
      setSelectedCurrency(options.find((c) => c.code === DEFAULT_CURRENCY) || options[0] || null);
    });

    fetchTaxes().then(({ taxes }) => {
      setTaxRates((taxes || []).reduce((map, rate) => ({ ...map, [rate.currency]: rate.tax }), {}));
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const brackets = bracketsFromPlan(selected_plan);
    setContactBrackets(brackets);
    setSelectedBracket(brackets[0] || null);
  }, [selected_plan]);

  // Start fresh each time the modal opens for a different organisation.
  useEffect(() => {
    if (!organization?.id) return;
    setShowingSummary(false);
    setSelectedPlan(modules[0] || null);
    setSelectedCurrency(
      currencies.find((c) => c.code === DEFAULT_CURRENCY) || currencies[0] || null
    );
    setSelectedRenewal(RENEWAL_PERIODS[0]);

    // A client can only run one subscription at a time — flag an existing one
    // so we block the purchase below.
    setCheckingSubscription(true);
    setHasActiveSubscription(false);
    fetchActiveSubscriptions({ query_string: `org_ids=${organization.id}` }).then(
      ({ subscriptions }) => {
        setHasActiveSubscription((subscriptions || []).length > 0);
        setCheckingSubscription(false);
      }
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organization?.id]);

  const currency_code = selected_currency?.code;
  const currency_symbol = getCurrencySymbol(currency_code) || '';
  const renewal = selected_renewal.value;

  const target_pricing = (selected_plan?.pricing || []).find(
    (pricing) =>
      selected_bracket &&
      pricing.contact_bracket.min === selected_bracket.min &&
      pricing.contact_bracket.max === selected_bracket.max
  );

  const base_amount =
    target_pricing?.prices?.find((p) => p.currency === currency_code)?.amount || 0;
  const gross_price = renewal === 'year' ? base_amount * 12 : base_amount;
  const discount_rate = agencyDiscountRate(selected_plan?.tier, renewal);
  // Discounted agency price, then VAT on top.
  const discounted_price = Math.round(gross_price * (1 - discount_rate));
  const tax_rate = tax_rates[currency_code] || 0;
  const vat = Math.round((discounted_price * tax_rate) / 100);
  const total = discounted_price + vat;

  const is_valid =
    !!selected_plan && !!selected_currency && !!selected_bracket && !!selected_renewal && total > 0;

  const money = (amount) => `${currency_symbol}${toReadableNumber(amount)}`;

  const proceedToPayment = async () => {
    if (has_active_subscription) return;

    const config = prepareCheckoutConfig({
      tenant: organization,
      amount: total,
      currency: selected_currency,
      tx_reason: 'direct'
    });

    // Mirror the purchase flow's payment_details so the backend can build the
    // subscription on payment verification (it reads metadata.module + pricing).
    const payment_details = {
      amount_to_pay: total,
      cost: gross_price,
      discount: gross_price - discounted_price,
      discount_rate: discount_rate * 100,
      discounted_price,
      renewal_period: renewal,
      currency: selected_currency,
      tax: vat,
      tax_rate,
      pricing: target_pricing
    };

    const data = {
      currency: currency_code,
      conversion_rate: selected_currency.exchange_rate,
      amount: total,
      tenant_id: organization.id,
      tx_ref: config.tx_ref,
      reason: 'subscription',
      metadata: {
        agent: {
          name: agency?.business_name,
          firstname: agency?.firstname,
          lastname: agency?.lastname,
          email: agency?.email,
          id: agency?.id
        },
        plan: { code: selected_plan.code, tier: selected_plan.tier, title: selected_plan.title },
        contact_bracket: { min: selected_bracket.min, max: selected_bracket.max },
        module: { ...selected_plan, pricing: target_pricing },
        vat,
        total,
        ...payment_details
      }
    };

    setProcessing(true);
    const payment = await createPayment({ data });
    setProcessing(false);

    if (!payment) return;
    if (window.FlutterwaveCheckout) window.FlutterwaveCheckout(config);
    closeModal();
  };

  if (has_active_subscription) {
    return (
      <InfoBanner
        type="error"
        message={
          <span>
            <strong>{organization.name}</strong> already has an active subscription. You can buy a
            new plan once the current one is cancelled or has expired.
          </span>
        }
      />
    );
  }

  if (showing_summary) {
    return (
      <div>
        <div className={styles.summaryHead}>
          {selected_plan?.title} — {selected_renewal.label} ({selected_bracket?.label} contacts)
        </div>
        <div className={styles.summaryRow}>
          <span>Discounted agency price</span>
          <span>{money(discounted_price)}</span>
        </div>
        <div className={styles.summaryRow}>
          <span>VAT ({tax_rate}%)</span>
          <span>{money(vat)}</span>
        </div>
        <div className={cx(styles.summaryRow, styles.summaryTotal)}>
          <span>Total</span>
          <span>{money(total)}</span>
        </div>

        <Spacer />
        <GridRow num_of_columns={2}>
          <GridColumn>
            <Button type="secondary" text="← Back" onClick={() => setShowingSummary(false)} />
          </GridColumn>
          <GridColumn>
            <Button text="Proceed to payment" loading={processing} onClick={proceedToPayment} />
          </GridColumn>
        </GridRow>
      </div>
    );
  }

  return (
    <form>
      <GridRow num_of_columns={2}>
        <GridColumn>
          <DetailItem title="Plan" required>
            <MultiSelect
              options={modules}
              value={selected_plan}
              onChange={setSelectedPlan}
              isSorted={false}
              placeholder="Select plan"
            />
          </DetailItem>
        </GridColumn>
        <GridColumn>
          <DetailItem title="Renewal Period" required>
            <MultiSelect
              options={RENEWAL_PERIODS}
              value={selected_renewal}
              onChange={setSelectedRenewal}
              isSorted={false}
            />
          </DetailItem>
        </GridColumn>
      </GridRow>
      <GridRow num_of_columns={2}>
        <GridColumn>
          <DetailItem title="Contact Bracket" required>
            <MultiSelect
              options={contact_brackets}
              value={selected_bracket}
              onChange={setSelectedBracket}
              isSorted={false}
              placeholder="Select bracket"
            />
          </DetailItem>
        </GridColumn>
        <GridColumn>
          <DetailItem title="Currency" required>
            <MultiSelect
              options={currencies}
              value={selected_currency}
              onChange={setSelectedCurrency}
              placeholder="Select currency"
            />
          </DetailItem>
        </GridColumn>
      </GridRow>

      <Spacer />
      <GridRow num_of_columns={3}>
        <GridColumn />
        <GridColumn />
        <GridColumn>
          <Button
            text="Buy"
            disabled={!is_valid || checking_subscription}
            onClick={() => setShowingSummary(true)}
          />
        </GridColumn>
      </GridRow>
    </form>
  );
};
